import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M12_ROLES, hasAnyRole } from '../../lib/roles';
import type { ChildCaseTargetModule, InvestmentDashboard } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

const TARGET_MODULES: ChildCaseTargetModule[] = ['M13_BUSINESS_REGISTRY', 'M14_LICENSING', 'M03_IMMIGRATION', 'M18_LAND_REGISTRY', 'M35_ENERGY_UTILITIES'];

export function InvestmentProjectDetail() {
  const { ref = '' } = useParams();
  const { persona } = useAuth();
  const isFacilitator = hasAnyRole(persona?.roles, M12_ROLES.INVESTMENT_FACILITATOR);
  const dashQ = useApiQuery<InvestmentDashboard>(`/api/v1/m12/investment-projects/${ref}/dashboard`);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (dashQ.loading) return <Spinner label="Loading project dashboard…" />;
  if (dashQ.error || !dashQ.data) {
    return (
      <div>
        <Link to="/trade/investment-projects" className="text-sm font-semibold text-brand-600 hover:underline">← Back to projects</Link>
        <div className="mt-4"><ErrorNote message={dashQ.error ?? 'Project not found'} /></div>
      </div>
    );
  }
  const { project, childCases, progress, incentiveCertificates, megaCase, megaCaseUnavailable } = dashQ.data;
  const isSelfApplicant =
    project.applicantType === 'ENTITY' ? persona?.actingForBusinessId === project.applicantId : persona?.nin === project.applicantId;
  const canWithdraw = (isFacilitator || isSelfApplicant) && !['OPERATIONAL', 'REJECTED', 'WITHDRAWN'].includes(project.state);

  const act = async (path: string, body?: unknown) => {
    setBusy(path);
    setError(null);
    try {
      await api.post(`/api/v1/m12/investment-projects/${ref}/${path}`, body);
      dashQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : `${path} failed`);
    } finally {
      setBusy(null);
    }
  };

  const withdraw = async () => {
    setBusy('withdraw');
    setError(null);
    try {
      await api.post(
        `/api/v1/m12/investment-projects/${ref}/withdraw`,
        { reason: 'Withdrawn by applicant' },
        { actingFor: project.applicantType === 'ENTITY' ? persona?.actingForBusinessId : undefined },
      );
      dashQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Withdraw failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <Link to="/trade/investment-projects" className="mb-3 inline-block text-sm font-semibold text-brand-600 hover:underline">← Back to projects</Link>
      <PageHeader
        title={`${project.investorName} — ${project.refNo}`}
        subtitle={`${project.sector.replaceAll('_', ' ')} · ${project.investorType} investor`}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={project.state} />
            {canWithdraw ? (
              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={withdraw} disabled={busy !== null}>
                {busy === 'withdraw' ? 'Withdrawing…' : 'Withdraw'}
              </button>
            ) : null}
          </div>
        }
      />

      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}

      <div className="mb-5 grid gap-3 sm:grid-cols-4">
        <StatCard label="Capex" value={`${scr(project.capexAmount)} ${project.capexCurrency}`} />
        <StatCard label="Jobs planned" value={`${project.employmentPlanLocalJobs} local / ${project.employmentPlanForeignJobs} foreign`} />
        <StatCard label="Child-case progress" value={`${progress.resolved}/${progress.total}`} tone={progress.total > 0 && progress.resolved === progress.total ? 'good' : 'default'} />
        <StatCard label="Mega-case" value={project.megaCaseRef ?? '—'} hint={megaCaseUnavailable ? 'FlowEngine unreachable' : undefined} />
      </div>

      <p className="card mb-5 p-4 text-sm text-slate-700">{project.description}</p>
      {project.rejectionReason ? <div className="mb-5"><ErrorNote message={project.rejectionReason} /></div> : null}

      {isFacilitator ? (
        <div className="card mb-5 p-5">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Facilitator actions</h2>
          <div className="flex flex-wrap gap-1.5">
            {project.state === 'SUBMITTED' ? (
              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => act('assign', { facilitatorId: persona!.sub })} disabled={busy !== null}>Assign myself</button>
            ) : null}
            {['SUBMITTED', 'UNDER_REVIEW'].includes(project.state) ? (
              <>
                <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => act('decide', { outcome: 'APPROVED', reason: 'Meets investment-incentive criteria' })} disabled={busy !== null}>Approve</button>
                <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => act('decide', { outcome: 'REJECTED', reason: 'Does not meet criteria' })} disabled={busy !== null}>Reject</button>
              </>
            ) : null}
            {['APPROVED', 'IN_DELIVERY'].includes(project.state) && progress.total === progress.resolved ? (
              <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => act('operational')} disabled={busy !== null}>Mark operational</button>
            ) : null}
          </div>
        </div>
      ) : null}

      {isFacilitator && ['APPROVED', 'IN_DELIVERY'].includes(project.state) ? (
        <OpenChildCaseForm projectRef={ref} onOpened={dashQ.reload} />
      ) : null}

      <div className="card mb-5 p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Child-case workstreams</h2>
        {childCases.length === 0 ? (
          <p className="text-xs text-slate-400">No child cases opened yet.</p>
        ) : (
          <ul className="space-y-2">
            {childCases.map((c) => (
              <li key={c.id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{c.targetModule.replaceAll('_', ' ')} — {c.workstreamLabel}</span>
                  <StatusBadge status={c.status} />
                </div>
                {c.externalCaseRef ? <div className="mt-1 text-xs text-slate-500">External case: {c.externalCaseRef}</div> : null}
                {isFacilitator ? <ChildCaseStatusForm projectRef={ref} childId={c.id} currentStatus={c.status} onChanged={dashQ.reload} /> : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {isFacilitator && project.state !== 'SUBMITTED' ? (
        <IncentiveCertificateSection projectRef={ref} certificates={incentiveCertificates} onChanged={dashQ.reload} />
      ) : null}
    </div>
  );
}

function OpenChildCaseForm({ projectRef, onOpened }: { projectRef: string; onOpened: () => void }) {
  const [showForm, setShowForm] = useState(false);
  const [targetModule, setTargetModule] = useState<ChildCaseTargetModule>('M14_LICENSING');
  const [workstreamLabel, setWorkstreamLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m12/investment-projects/${projectRef}/child-cases`, { targetModule, workstreamLabel });
      setShowForm(false);
      setWorkstreamLabel('');
      onOpened();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not open child case');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card mb-5 p-5">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">Open a child-case workstream</h2>
        <button className="text-xs font-semibold text-brand-600 hover:underline" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ Open'}</button>
      </div>
      {showForm ? (
        <div className="space-y-2">
          {error ? <ErrorNote message={error} /> : null}
          <div className="grid grid-cols-2 gap-2">
            <Field label="Target module" className="!mb-0">
              <select className="field-input" value={targetModule} onChange={(e) => setTargetModule(e.target.value as ChildCaseTargetModule)}>
                {TARGET_MODULES.map((m) => <option key={m} value={m}>{m.replaceAll('_', ' ')}</option>)}
              </select>
            </Field>
            <Field label="Workstream label" className="!mb-0"><input className="field-input" value={workstreamLabel} onChange={(e) => setWorkstreamLabel(e.target.value)} placeholder="e.g. Tourism accommodation licence" /></Field>
          </div>
          <button className="btn-primary !py-1 text-xs" onClick={open} disabled={busy || workstreamLabel.length < 3}>{busy ? 'Opening…' : 'Open workstream'}</button>
        </div>
      ) : null}
    </div>
  );
}

function ChildCaseStatusForm({ projectRef, childId, currentStatus, onChanged }: { projectRef: string; childId: string; currentStatus: string; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const resolved = ['COMPLETED', 'REJECTED', 'WAIVED'].includes(currentStatus);
  if (resolved) return null;

  const next: Record<string, string[]> = {
    PENDING_REFERRAL: ['REFERRED', 'WAIVED'],
    REFERRED: ['IN_PROGRESS', 'REJECTED', 'WAIVED'],
    IN_PROGRESS: ['COMPLETED', 'REJECTED', 'WAIVED'],
  };
  const options = next[currentStatus] ?? ['COMPLETED', 'REJECTED', 'WAIVED'];

  const setStatus = async (status: string) => {
    setBusy(status);
    setError(null);
    try {
      await api.post(`/api/v1/m12/investment-projects/${projectRef}/child-cases/${childId}/status`, { status });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not update status');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-2">
      {error ? <ErrorNote message={error} /> : null}
      <div className="flex flex-wrap gap-1.5">
        {options.map((s) => (
          <button key={s} className="btn-secondary !px-2 !py-0.5 text-[11px]" onClick={() => setStatus(s)} disabled={busy !== null}>
            {busy === s ? '…' : `→ ${s.replaceAll('_', ' ')}`}
          </button>
        ))}
      </div>
    </div>
  );
}

function IncentiveCertificateSection({ projectRef, certificates, onChanged }: { projectRef: string; certificates: InvestmentDashboard['incentiveCertificates']; onChanged: () => void }) {
  const [showForm, setShowForm] = useState(false);
  const [grantedIncentives, setGrantedIncentives] = useState('');
  const [validityMonths, setValidityMonths] = useState('60');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const issue = async () => {
    setBusy('issue');
    setError(null);
    try {
      await api.post(`/api/v1/m12/investment-projects/${projectRef}/incentive-certificate`, {
        grantedIncentives: grantedIncentives.split(',').map((s) => s.trim()).filter(Boolean),
        validityMonths: Number.parseInt(validityMonths, 10),
      });
      setShowForm(false);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not issue certificate');
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (certId: string) => {
    setBusy(certId);
    setError(null);
    try {
      await api.post(`/api/v1/m12/investment-projects/${projectRef}/incentive-certificate/${certId}/revoke`, { reason: 'Revoked by facilitator' });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Revoke failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card p-5">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">Investment-incentive certificates</h2>
        <button className="text-xs font-semibold text-brand-600 hover:underline" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ Issue'}</button>
      </div>
      {error ? <div className="mb-2"><ErrorNote message={error} /></div> : null}
      {showForm ? (
        <div className="mb-3 space-y-2 rounded-lg border border-slate-200 p-3">
          <Field label="Granted incentives (comma separated)" className="!mb-0"><input className="field-input" value={grantedIncentives} onChange={(e) => setGrantedIncentives(e.target.value)} placeholder="Business tax holiday, Import duty exemption" /></Field>
          <Field label="Validity (months)" className="!mb-0"><input className="field-input w-32" type="number" value={validityMonths} onChange={(e) => setValidityMonths(e.target.value)} /></Field>
          <button className="btn-primary !py-1 text-xs" onClick={issue} disabled={busy !== null || grantedIncentives.trim().length === 0}>{busy === 'issue' ? 'Issuing…' : 'Issue certificate (raises fee intent)'}</button>
        </div>
      ) : null}
      {certificates.length === 0 ? <p className="text-xs text-slate-400">None issued yet.</p> : (
        <ul className="space-y-2">
          {certificates.map((c) => (
            <li key={c.id} className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
              <div className="flex items-center justify-between">
                <span>{c.grantedIncentives.join(', ')}</span>
                <StatusBadge status={c.state} />
              </div>
              {c.state === 'ISSUED' ? (
                <button className="btn-danger mt-1 !px-2 !py-0.5 text-[11px]" onClick={() => revoke(c.id)} disabled={busy !== null}>Revoke</button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
